import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { stripeApi } from '@/services/api/stripeApi';
import { restaurantApi } from '@/services/api/restaurantApi';
import Logo from '@/components/Logo';
import { API_BASE_URL } from '@/config/api';
import Cookies from 'js-cookie';

const PaymentSuccessSales = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  useEffect(() => {
    console.log('🎬 SALES-ASSISTED ACTIVATION: Payment success page loaded');

    // Clean up activation tokens
    const activationToken = localStorage.getItem('activation_session_token');
    const planSelectionToken = localStorage.getItem('plan_selection_session_token');

    if (activationToken || planSelectionToken) {
      console.log('🎬 Cleaning up activation tokens');
      localStorage.removeItem('activation_session_token');
      localStorage.removeItem('plan_selection_session_token');
      localStorage.removeItem('activation_rest_id');
      localStorage.removeItem('activation_restaurant_name');
      localStorage.removeItem('activation_oauth_provider');
      console.log('✅ Activation tokens cleared');
    }

    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      // Poll for subscription with retry logic
      // Webhook may take a few seconds to process and create subscription
      let attempts = 0;
      const maxAttempts = 10; // 10 attempts = 20 seconds max
      const pollInterval = 2000; // 2 seconds between attempts

      const checkSubscription = async () => {
        try {
          console.log(`🔄 Polling for subscription (attempt ${attempts + 1}/${maxAttempts})...`);

          const [sessionData, profileData, subData] = await Promise.all([
            stripeApi.getCheckoutSession(sessionId),
            restaurantApi.getProfile(), // Get user profile for email
            fetch(`${API_BASE_URL}/subscription/current`, {
              headers: {
                'Authorization': `Bearer ${Cookies.get('auth_token')}`,
              }
            }).then(res => res.json())
          ]);

          // Check if subscription was created
          if (subData && subData.subscription_id) {
            // Subscription found!
            console.log('✅ Subscription found:', subData);
            setSessionDetails(sessionData);
            setSubscriptionInfo(subData);
            setUserEmail(profileData.email || sessionData.customer_email || '');
            setLoading(false);

            console.log('🔍 Subscription data received:', {
              is_trial: subData?.is_trial,
              subscription_status: subData?.subscription_status,
              plan_name: subData?.plan_name
            });

            const hasTrial = (subData?.plan_name?.toLowerCase() === 'delicious') &&
                            (subData?.is_trial === true || subData?.subscription_status === 'trialing');

            toast({
              title: "Welcome to Dine AI!",
              description: hasTrial
                ? "Your 30-day free trial has started. Your restaurant is ready!"
                : "Your subscription is active. Your restaurant is ready!",
            });
          } else if (attempts < maxAttempts) {
            // Subscription not found yet, retry
            attempts++;
            console.log(`⏳ Subscription not ready, retrying in ${pollInterval/1000}s...`);
            setTimeout(checkSubscription, pollInterval);
          } else {
            // Max attempts reached
            console.warn('⚠️ Subscription not found after max attempts');
            setLoading(false);
            toast({
              title: "Payment Processing",
              description: "Your payment was successful! Your subscription is being activated and will be ready shortly.",
            });

            // Still set some basic info so page isn't totally empty
            setSessionDetails(sessionData);
            setUserEmail(profileData.email || sessionData.customer_email || '');
          }
        } catch (error) {
          console.error('Error verifying session:', error);

          if (attempts < maxAttempts) {
            // Retry on error
            attempts++;
            setTimeout(checkSubscription, pollInterval);
          } else {
            setLoading(false);
            toast({
              title: "Error",
              description: "Unable to verify payment. Please contact support.",
              variant: "destructive"
            });
          }
        }
      };

      checkSubscription();
    } else {
      setLoading(false);
    }
  }, [searchParams, toast]);

  const handleContinue = async () => {
    console.log('✅ SALES-ASSISTED: Refreshing JWT context before dashboard navigation');

    // Clean up activation-related storage
    localStorage.removeItem('activation_session_token');
    localStorage.removeItem('plan_selection_session_token');
    localStorage.removeItem('activation_rest_id');
    localStorage.removeItem('activation_restaurant_name');
    localStorage.removeItem('activation_oauth_provider');

    try {
      // Refresh JWT to get updated context (restaurant info linked by webhook)
      console.log('🔄 Calling refresh-context endpoint...');
      const refreshResponse = await fetch(`${API_BASE_URL}/v1/auth/refresh-context`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        console.log('✅ JWT context refreshed:', refreshData.user_context);

        // Store the new JWT token with updated restaurant context
        Cookies.set('auth_token', refreshData.token, {
          expires: 1, // 1 day
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
          path: '/'
        });

        console.log('✅ New JWT stored, navigating to dashboard');
      } else {
        console.warn('⚠️ Could not refresh JWT context, proceeding anyway');
      }
    } catch (error) {
      console.error('Error refreshing JWT context:', error);
      // Continue even if refresh fails - dashboard will handle it
    }

    // Navigate to dashboard with fresh JWT
    window.location.href = '/restaurant-dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Verifying your subscription...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo className="text-white h-10" />
          </div>

          {/* Success Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              Welcome to Dine AI! 🎉
            </h1>

            <p className="text-xl text-white/80 mb-8">
              {(subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
               (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing'))
                ? 'Your 30-day free trial has started successfully.'
                : 'Your subscription has been activated successfully.'}
            </p>

            {sessionDetails && (
              <div className="bg-white/5 rounded-lg p-6 mb-8 text-left">
                <h3 className="text-lg font-semibold text-white mb-3">Subscription Details:</h3>
                <div className="space-y-2 text-white/70">
                  <p>Email: {userEmail}</p>
                  <p>Plan: {subscriptionInfo?.plan_name ? subscriptionInfo.plan_name.charAt(0).toUpperCase() + subscriptionInfo.plan_name.slice(1) : 'Loading...'}</p>
                  <p>Status: {(subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
                     (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing'))
                    ? 'Active Trial (30 days)'
                    : 'Active Subscription'}</p>
                  <p>Next billing date: {subscriptionInfo?.current_period_end
                    ? new Date(subscriptionInfo.current_period_end).toLocaleDateString()
                    : 'Loading...'}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Button
                onClick={handleContinue}
                className="w-full py-6 text-lg bg-[#FDB913] hover:bg-[#FDB913]/90 text-black font-semibold"
              >
                Go to Dashboard
              </Button>

              {subscriptionInfo?.plan_name?.toLowerCase() === 'delicious' &&
               (subscriptionInfo?.is_trial === true || subscriptionInfo?.subscription_status === 'trialing') && (
                <p className="text-sm text-white/60">
                  You won't be charged during your trial period. Cancel anytime.
                </p>
              )}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center text-white/60 text-sm">
            <p>Need help? Contact us at support@dineai.ai</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessSales;
